import React, { useCallback, useEffect, useState } from "react";
import {
  LoadingIndicator,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useMediaQuery } from "react-responsive";
import { SplatContact } from "../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { CheckoutFooter } from "../molecules/CheckoutFooter";
import { TaskHeader } from "../molecules/TaskHeader";
import { ROLES } from "../../../utils/usageUtils";
import { UnknownOdiModal } from "../molecules/UnknownOdiModal";
import { IntervalDataCard } from "../molecules/IntervalDataCard";
import { DocumentCard } from "./DocumentCard";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { useCombinedEnvelopeStatus } from "checkout/hooks/useCombinedEnvelopeStatus";
import { useReadSignDocs } from "checkout/hooks/useReadSignDocs";
import { useContactsData } from "utils/swrHooks";
import { HyperlinkButton } from "components/atoms/HyperlinkButton";
import documentIcon from "assets/images/document.svg";
import { getPrimaryContact } from "utils/contacts";
import { patchUsage } from "services/prospect";
import { useAuthToken } from "hooks/useAuthToken";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { Mixpanel } from "providers/mixpanel";
import { CUSTOMER_SELF_CHECKOUT__CLICKED_CONFIRM_CTA_READ_AND_SIGN_TAB_CHECKOUT_PAGE } from "constants/mixpanelEvents";
import { subscribeToAsyncRequest } from "utils/subscribeToAsyncRequest";
import { useUsageData } from "utils/swrHooks";
import { ContractReviewBanners } from "components/molecules/ContractReviewBanners";
import { isTrustAgreementNameMatchMessage } from "utils/contractReviewUtils";

const ReadAndSignDocsTaskView: React.FC = () => {
  const hybridToken = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const {
    tasksLoading,
    currentTask,
    proposalId,
    prospectId,
    refreshTasks,
    navigateToNextTask,
    navigateToTask,
    isNewHomes,
    contractReviewMessages,
  } = useCheckoutContext();
  const isCustomer = !!proposalToken;
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const { combinedEnvelopeStatus, optimisticRefreshCombinedEnvelopeStatus } =
    useCombinedEnvelopeStatus(prospectId, proposalId);
  const { readSignDocsSteps, combinedDocsLock, coborrower } = useReadSignDocs({
    prospectId,
    proposalId,
  });

  const [showUnknownOdisModal, setShowUnknownOdisModal] = useState(true);
  const unknownOdis = readSignDocsSteps?.reduce(
    (stepNames, step) =>
      step.isUnknown ? [...stepNames, step.docName] : stepNames,
    [] as string[]
  );
  const { contacts, isContactsLoading } = useContactsData(
    prospectId,
    hybridToken,
    proposalToken
  );
  const [secondaryContact, setSecondaryContact] = useState<
    SplatContact | undefined
  >();

  useEffect(() => {
    if (contacts) {
      setSecondaryContact(
        contacts.find((c) => c.derivedRoles.includes(ROLES.CONTRACTCOSIGNER))
      );
    }
  }, [contacts]);

  const [showOptionalSteps, setShowOptionalSteps] = useState(false);

  const onClickConfirm = useCallback(() => {
    if (isCustomer) {
      Mixpanel.track(
        CUSTOMER_SELF_CHECKOUT__CLICKED_CONFIRM_CTA_READ_AND_SIGN_TAB_CHECKOUT_PAGE,
        {
          prospectId,
          proposalId,
        }
      );
    }
    navigateToNextTask();
  }, [isCustomer, prospectId, proposalId, navigateToNextTask]);

  const enableContinue = readSignDocsSteps
    ?.filter((doc) => doc.isRequired)
    .every((doc) => doc.completed);

  const selectedContacts =
    readSignDocsSteps?.reduce((arr, curr) => {
      if (arr?.[curr.primarySignerRole] || curr.isUnknown) return arr;

      return {
        ...arr,
        [curr.primarySignerRole]: contacts?.find(
          (contact) =>
            contact.roles.includes(curr.primarySignerRole) &&
            (curr.primarySignerRole === ROLES.HOMEOWNER
              ? contact.primary
              : true)
        ),
      };
    }, {} as Record<string, SplatContact | undefined>) || {};

  // Get prospect data like in ConfirmUtilityTaskView
  const { prospect: usage, isLoading: isUsageLoading } =
    useUsageData(prospectId);

  const [intervalDataDeclineReason, setIntervalDataDeclineReason] =
    useState("");
  const [isUpdatingIntervalData, setIsUpdatingIntervalData] = useState(false);

  const handleIntervalDataUpdate = useCallback(async () => {
    setIsUpdatingIntervalData(true);

    try {
      const prospectBody = {
        intervalDataDeclinedReason: intervalDataDeclineReason,
      };

      const result = await patchUsage(hybridToken, prospectId, prospectBody);
      await subscribeToAsyncRequest(hybridToken, result.asyncRequestId);

      // Refresh tasks to update completion status
      refreshTasks();
    } catch (error) {
      console.error("Error updating interval data decline reason:", error);
    } finally {
      setIsUpdatingIntervalData(false);
    }
  }, [hybridToken, prospectId, intervalDataDeclineReason, refreshTasks]);

  const intervalDataDeclineReasonStep = readSignDocsSteps?.find(
    (step) => step.odiName === "intervalData"
  );

  const hasMissingContacts = Object.values(selectedContacts).some(
    (contact) => !contact
  );

  // Initialize the state with current prospect value
  useEffect(() => {
    if (usage) {
      setIntervalDataDeclineReason(usage.intervalDataDeclinedReason || "");
    }
  }, [usage]);

  const showFooter =
    !!currentTask &&
    !isContactsLoading &&
    !isUsageLoading &&
    !hasMissingContacts;

  useCheckoutFooter(
    showFooter ? (
      <CheckoutFooter
        buttonText="Continue"
        isDisabled={!enableContinue}
        onClick={onClickConfirm}
      />
    ) : null,
    [showFooter, enableContinue, onClickConfirm]
  );

  if (
    !currentTask ||
    isContactsLoading ||
    isUsageLoading ||
    hasMissingContacts
  ) {
    return (
      <LoadingWrapper>
        <LoadingIndicator color="black" />
      </LoadingWrapper>
    );
  }

  const toggleShowOptionalSteps = () => {
    setShowOptionalSteps((prevShowOptionalSteps) => !prevShowOptionalSteps);
  };

  const ipaStep = readSignDocsSteps?.find(
    (step) => step.odiName === "ipaRebate"
  );

  const cpucStep = readSignDocsSteps?.find((step) => step.odiName === "cpuc");
  const customerAgreementStep = readSignDocsSteps?.find(
    (step) => step.odiName === "customerAgreement"
  );

  const optionalSteps = readSignDocsSteps?.filter((step) => step.isOptional);
  const optionalStepsDoneLoading =
    !tasksLoading && optionalSteps && optionalSteps.length > 0;

  return (
    <>
      <Wrapper data-testid="read-sign-docs-task-view" isMobile={isMobile}>
        {unknownOdis && unknownOdis.length > 0 && showUnknownOdisModal && (
          <UnknownOdiModal
            odiNames={unknownOdis}
            onClose={() => {
              setShowUnknownOdisModal(false);
            }}
          />
        )}
        <Typography>
          Please read the agreements carefully. You will then be redirected to
          Docusign for signature. It may take several minutes to update. If you
          already signed, please{" "}
          <HyperlinkButton
            onClick={() => {
              refreshTasks();
            }}
          >
            refresh this page.
          </HyperlinkButton>
        </Typography>
        {/* Render IL IPA first so it remains the first required action */}
        {!tasksLoading && ipaStep && (
          <DocumentCard
            key={ipaStep?.odiName}
            primaryContact={getPrimaryContact(contacts)!}
            refreshTasks={refreshTasks}
            {...ipaStep}
          />
        )}
        {["ENABLED", "SENT"].includes(combinedEnvelopeStatus || "") && (
          <DocumentCard
            allowSecondaryContact
            cardDataTestId="send-all-documents-card"
            completed={false}
            description="Send all required and unsigned documents in one unified and simplified signing experience. Or you can sign each required document individually below."
            docName="Send all documents"
            hideCardSectionDivider
            hideContactSection
            hideStatusChip={combinedDocsLock.isUnlocked}
            isUnlocked={combinedDocsLock.isUnlocked}
            isCombinedAgreement
            inProgress={combinedEnvelopeStatus === "SENT"}
            primaryContact={getPrimaryContact(contacts)!}
            secondaryContact={secondaryContact}
            primarySignerRole={ROLES.HOMEOWNER}
            refreshTasks={refreshTasks}
            showEmail={true}
            showSMS={true}
            documentType="Combined Docs"
            lockedTooltip={combinedDocsLock.lockedTooltip}
            refreshCombinedEnvelopeStatus={
              optimisticRefreshCombinedEnvelopeStatus
            }
          />
        )}
        <ContractReviewBanners
          messages={contractReviewMessages}
          category="customer_name_check"
          filter={(message) => !isTrustAgreementNameMatchMessage(message)}
          actionLink={{
            text: "Edit signers",
            onClick: () => navigateToTask("confirmNames"),
          }}
          sessionDismissScopeId={proposalId}
        />
        {tasksLoading && <LoadingIndicator color="black" />}
        {/* Replace the DocumentCard with custom Select component */}
        {!isNewHomes &&
          !tasksLoading &&
          intervalDataDeclineReasonStep &&
          usage && (
            <IntervalDataCard
              intervalDataDeclineReason={intervalDataDeclineReason}
              setIntervalDataDeclineReason={setIntervalDataDeclineReason}
              completed={intervalDataDeclineReasonStep.completed}
              isUpdatingIntervalData={isUpdatingIntervalData}
              handleIntervalDataUpdate={handleIntervalDataUpdate}
              intervalDataUsed={usage.intervalDataUsed || false}
            />
          )}
        {/* Render Cpuc Doc */}
        {!tasksLoading && cpucStep && (
          <DocumentCard
            key={cpucStep.odiName}
            primaryContact={selectedContacts?.[cpucStep.primarySignerRole]!}
            secondaryContact={secondaryContact}
            setSecondaryContact={setSecondaryContact}
            refreshTasks={refreshTasks}
            {...cpucStep}
          />
        )}
        {/* Render Customer Agreement Doc */}
        {!tasksLoading && customerAgreementStep && (
          <DocumentCard
            key={customerAgreementStep.odiName}
            primaryContact={
              selectedContacts?.[customerAgreementStep.primarySignerRole]!
            }
            secondaryContact={secondaryContact}
            setSecondaryContact={setSecondaryContact}
            refreshTasks={refreshTasks}
            {...customerAgreementStep}
          />
        )}
        {/* Render Docs ODIs */}
        {!tasksLoading &&
          readSignDocsSteps
            ?.filter(
              (step) =>
                step.odiName !== "ipaRebate" &&
                step.odiName !== "customerAgreement" &&
                step.odiName !== "cpuc" &&
                step.odiName !== "intervalData" &&
                !step.isOptional &&
                !step.isUnknown
            )
            .map((step, index) => (
              <DocumentCard
                key={index}
                primaryContact={selectedContacts?.[step.primarySignerRole]!}
                secondaryContact={
                  step.odiName === "loanDocumentSignature"
                    ? coborrower
                    : undefined
                }
                refreshTasks={refreshTasks}
                {...step}
              />
            ))}
        {optionalStepsDoneLoading && (
          <OptionalStepsWrapper isMobile={isMobile}>
            <OptionalStepsHeaderWrapper>
              <TaskHeader
                icon={documentIcon}
                label={"Optional Supplementary Documents"}
              />
              <div
                onClick={toggleShowOptionalSteps}
                data-testid="checkout-read-and-sign-net-task-metering-header-carrot"
              >
                <SvgIcon
                  name={
                    showOptionalSteps
                      ? SvgNames.ChevronUp
                      : SvgNames.ChevronDown
                  }
                  width="24"
                  height="24"
                  color={tokens.BLACK}
                  style={{ marginTop: "24px" }}
                />
              </div>
            </OptionalStepsHeaderWrapper>

            {showOptionalSteps &&
              optionalSteps &&
              optionalSteps.map((step) => (
                <DocumentCard
                  key={step.odiName}
                  primaryContact={selectedContacts?.[step.primarySignerRole]!}
                  secondaryContact={undefined}
                  refreshTasks={refreshTasks}
                  {...step}
                />
              ))}
          </OptionalStepsWrapper>
        )}
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const LoadingWrapper = styled.div`
  margin-top: 32px;
`;

const OptionalStepsWrapper = styled.div<{ isMobile?: boolean }>`
  border-top: 1px solid ${tokens.BORDER_LIGHT};
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ isMobile }) =>
    isMobile &&
    `
    padding-top: 24px;
  `}
`;

const OptionalStepsHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export { ReadAndSignDocsTaskView };
